import { type PropsWithChildren, useCallback, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'

import { ResponsiveSizeV2 } from '@/components/atomic/_enums'
import MobileTopDownloadBanner from '@/components/atomic/organisms/MobileTopDownloadBanner'
import Screen from '@/components/atomic/templates/Screen'
import { postAmplitude } from '@/core/lib'
import { useGlobalState } from '@/hooks/GlobalState'
import { useTranslation } from '@/lang'
import theme from '@/theme'
import { isTargetUtmUser } from '@/features/shared/utils/jp-ads-banner-control'
import { useLocales } from '@/shared/hooks/useLocales'

import type { GlobalNavBarTabKey } from '@/components/atomic/organisms/GlobalNavBar'

interface Props {
  userProfileImage?: string
  isRootScreen?: boolean
  isOpenSearch?: boolean
  isTopDownloadBannerHidden?: boolean
  isGlobalHeaderHidden?: boolean
  useGnb?: boolean
  useFooter?: boolean
  useSignIn?: boolean
  selectedGnbTabKey?: GlobalNavBarTabKey
  bottomMargin?: string
  bottomPaddingToAdd?: string
  onChangeOpenSearchState?: (isOpenSearch: boolean) => void
}

const TopDownloadScreen = ({
  isRootScreen = false,
  isTopDownloadBannerHidden,
  isGlobalHeaderHidden = false,
  children,
  userProfileImage,
  isOpenSearch,
  useGnb,
  useFooter,
  useSignIn,
  selectedGnbTabKey,
  bottomMargin,
  bottomPaddingToAdd,
  onChangeOpenSearchState,
}: PropsWithChildren<Props>): JSX.Element => {
  const { t } = useTranslation()
  const { topDownloadBanner, topDownloadBannerDispatch } = useGlobalState()
  const { isLocale } = useLocales()

  // JP 환경에서 타겟 광고(line_ads 등) 유입 유저인지 체크
  const isJpUtmUser = useMemo(() => {
    if (!isLocale.JP) return false

    // 타겟 utm_source 체크 (첫 방문 시 URL 직접 체크 + localStorage 체크)
    return isTargetUtmUser()
  }, [isLocale.JP])

  const onClickClose = useCallback(() => {
    setHiddenBannerExpiredTime()
    topDownloadBannerDispatch({ type: 'HIDE' })
    postAmplitude('click_header_close', {
      path: `${location.pathname}${location.search}`,
    })
  }, [topDownloadBannerDispatch])

  useEffect(() => {
    if (isValidHiddenBannerExpiredTime()) {
      topDownloadBannerDispatch({ type: 'HIDE' })
    }
  }, [topDownloadBannerDispatch])

  useEffect(() => {
    if (isTopDownloadBannerHidden || isJpUtmUser) {
      topDownloadBannerDispatch({ type: 'HIDE' })
    }
  }, [isTopDownloadBannerHidden, isJpUtmUser, topDownloadBannerDispatch])

  const showPageBanner = useCallback(() => {
    if (topDownloadBanner.isHidden) {
      return <></>
    }

    return (
      <MobileTopDownloadBanner
        description={t('common.header.download.description', { users: 65 })}
        downloadText={t('common.header.download.button')}
        onClickClose={onClickClose}
      />
    )
  }, [onClickClose, t, topDownloadBanner.isHidden])

  return (
    <StyledScreen
      $isHiddenTopDownloadBanner={topDownloadBanner.isHidden}
      isRootScreen={isRootScreen}
      onChangeOpenSearchState={onChangeOpenSearchState}
      pageBanner={showPageBanner()}
      userProfileImage={userProfileImage}
      isOpenSearch={isOpenSearch}
      isGlobalHeaderHidden={isGlobalHeaderHidden}
      useGnb={useGnb}
      useFooter={useFooter}
      useSignIn={useSignIn}
      selectedGnbTabKey={selectedGnbTabKey}
      bottomMargin={bottomMargin}
      bottomPaddingToAdd={bottomPaddingToAdd}
    >
      {children}
    </StyledScreen>
  )
}

const StyledScreen = styled(Screen)<{ $isHiddenTopDownloadBanner: boolean }>`
  #screenMain {
    @media screen and (max-width: ${ResponsiveSizeV2.DESKTOP_MIN_WIDTH}px) {
      ${({ $isHiddenTopDownloadBanner }) =>
        !$isHiddenTopDownloadBanner &&
        css`
          min-height: calc(
            100vh - ${theme.variables.globalHeaderHeight}px - ${theme.variables.topDownloadBannerHeight}px
          );
        `}
    }
  }
`

function isValidHiddenBannerExpiredTime(): boolean {
  const expiredTime = localStorage.getItem('hideTopDownloadBanner')
  return expiredTime !== null && Number(expiredTime) > Date.now()
}

function setHiddenBannerExpiredTime(): void {
  const expiredTime = Date.now() + 1000 * 60 * 60 * 24 * 10
  localStorage.setItem('hideTopDownloadBanner', `${expiredTime}`)
}

export default TopDownloadScreen
